import { create } from 'zustand';
import { creativeChatApi } from '../services/user/creativeChat.api';
import {
  ICreativeConversation,
  ICreativeMessage,
  CreativeMessageRole,
} from '@packages/global';

interface CreativeChatState {
  conversations: ICreativeConversation[];
  activeConversationId: string | null;
  messages: ICreativeMessage[];
  selectedModelId: string | null;
  loadingConversations: boolean;
  loadingMessages: boolean;
  isGenerating: boolean;
  streamingContent: string;
  _abortFn: (() => void) | null;

  loadConversations: () => Promise<void>;
  createConversation: (title?: string) => Promise<ICreativeConversation | null>;
  deleteConversation: (id: string) => Promise<void>;
  renameConversation: (id: string, title: string) => Promise<void>;
  selectConversation: (id: string) => Promise<void>;
  setSelectedModelId: (modelId: string) => void;
  sendMessage: (content: string) => void;
  deleteMessage: (messageId: string) => void;
  clearMessages: () => void;
  appendStreamContent: (content: string) => void;
  finishStreaming: (fullContent: string) => void;
  abortStream: () => void;
}

export const useCreativeChatStore = create<CreativeChatState>()((set, get) => ({
  conversations: [],
  activeConversationId: null,
  messages: [],
  selectedModelId: null,
  loadingConversations: false,
  loadingMessages: false,
  isGenerating: false,
  streamingContent: '',
  _abortFn: null,

  loadConversations: async () => {
    set({ loadingConversations: true });
    try {
      const conversations = await creativeChatApi.getConversations();
      set({ conversations, loadingConversations: false });
    } catch (error) {
      console.error('Failed to load conversations:', error);
      set({ loadingConversations: false });
    }
  },

  createConversation: async (title?: string) => {
    try {
      const conv = await creativeChatApi.createConversation({
        title,
        modelId: get().selectedModelId || undefined,
      });
      set((state) => ({
        conversations: [conv, ...state.conversations],
        activeConversationId: conv.id,
        messages: [],
      }));
      return conv;
    } catch (error) {
      console.error('Failed to create conversation:', error);
      return null;
    }
  },

  deleteConversation: async (id: string) => {
    try {
      await creativeChatApi.deleteConversation(id);
      set((state) => {
        const isActive = state.activeConversationId === id;
        return {
          conversations: state.conversations.filter((c) => c.id !== id),
          ...(isActive ? { activeConversationId: null, messages: [] } : {}),
        };
      });
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  },

  renameConversation: async (id: string, title: string) => {
    try {
      await creativeChatApi.updateConversation(id, { title });
      set((state) => ({
        conversations: state.conversations.map((c) =>
          c.id === id ? { ...c, title } : c
        ),
      }));
    } catch (error) {
      console.error('Failed to rename conversation:', error);
    }
  },

  selectConversation: async (id: string) => {
    set({ activeConversationId: id, loadingMessages: true });
    // 恢复会话的 modelId
    const conv = get().conversations.find((c) => c.id === id);
    if (conv?.modelId) {
      set({ selectedModelId: conv.modelId });
    }
    try {
      const messages = await creativeChatApi.getMessages(id);
      set({ messages, loadingMessages: false });
    } catch (error) {
      console.error('Failed to load messages:', error);
      set({ loadingMessages: false });
    }
  },

  setSelectedModelId: (modelId: string) => {
    set({ selectedModelId: modelId });
    const { activeConversationId } = get();
    if (activeConversationId) {
      creativeChatApi
        .updateConversation(activeConversationId, { modelId })
        .catch((err) => console.error('Failed to persist modelId:', err));
    }
  },

  deleteMessage: (messageId: string) => {
    const { activeConversationId } = get();
    // 乐观删除
    set((state) => ({
      messages: state.messages.filter((m) => m.id !== messageId),
    }));
    // 临时消息不调后端
    if (messageId.startsWith('temp-') || messageId.startsWith('ai-')) return;
    if (activeConversationId) {
      creativeChatApi
        .deleteMessage(activeConversationId, messageId)
        .catch((err) => console.error('Failed to delete message:', err));
    }
  },

  clearMessages: () => {
    const { activeConversationId } = get();
    if (!activeConversationId) return;
    set({ messages: [] });
    creativeChatApi
      .clearMessages(activeConversationId)
      .catch((err) => console.error('Failed to clear messages:', err));
  },

  sendMessage: (content: string) => {
    // 中止已有的流
    get().abortStream();

    const { activeConversationId, selectedModelId } = get();
    if (!activeConversationId) return;

    // 乐观更新：追加用户消息
    const now = new Date();
    const userMessage: ICreativeMessage = {
      id: `temp-${Date.now()}`,
      conversationId: activeConversationId,
      role: CreativeMessageRole.USER,
      content,
      createdAt: now,
      updatedAt: now,
    };
    set((state) => ({
      messages: [...state.messages, userMessage],
      isGenerating: true,
      streamingContent: '',
    }));

    // 发起 SSE 请求
    const { abort } = creativeChatApi.streamMessage(
      activeConversationId,
      content,
      selectedModelId || undefined,
      {
        onDelta: (delta) => {
          get().appendStreamContent(delta);
        },
        onDone: (fullContent) => {
          get().finishStreaming(fullContent);
        },
        onError: () => {
          set({ isGenerating: false, streamingContent: '' });
        },
      }
    );
    set({ _abortFn: abort });
  },

  appendStreamContent: (content: string) => {
    set((state) => ({ streamingContent: state.streamingContent + content }));
  },

  finishStreaming: (fullContent: string) => {
    const { activeConversationId } = get();
    const now = new Date();
    const assistantMessage: ICreativeMessage = {
      id: `ai-${Date.now()}`,
      conversationId: activeConversationId || '',
      role: CreativeMessageRole.ASSISTANT,
      content: fullContent,
      createdAt: now,
      updatedAt: now,
    };
    set((state) => {
      // 将会话移到列表顶部并更新 updatedAt
      const updated = state.conversations.map((c) =>
        c.id === state.activeConversationId ? { ...c, updatedAt: now } : c
      );
      const active = updated.find((c) => c.id === state.activeConversationId);
      const rest = updated.filter((c) => c.id !== state.activeConversationId);
      return {
        messages: [...state.messages, assistantMessage],
        streamingContent: '',
        isGenerating: false,
        _abortFn: null,
        conversations: active ? [active, ...rest] : updated,
      };
    });
  },

  abortStream: () => {
    const { _abortFn } = get();
    if (_abortFn) _abortFn();
    set({ isGenerating: false, streamingContent: '', _abortFn: null });
  },
}));
